import type { ComponentType } from "react";
import {
  LayoutDashboard, Users, ImageIcon, Images, UserCog, Settings, Package,
  ScrollText, LifeBuoy, Bug, SlidersHorizontal, Palette, MonitorPlay, BarChart3,
  MessageSquareText, ClipboardCheck, Store, Printer, ShieldCheck,
} from "lucide-react";
import type { RoutePath } from "@wedding/shared";
import { paths } from "./paths";
import { WeddingsPage } from "../pages/weddings";
import { UsersPage } from "../pages/users";
import { UserDetailPage } from "../pages/user-detail";
import { PlansPage } from "../pages/plans";
import { AuditLogPage } from "../pages/audit-log";
import { AdminSupportPage, AdminSupportDetailPage } from "../pages/support";
import { MaintenancePage, ErrorGroupDetailPage } from "../pages/maintenance";
import { ControlCenterPage } from "../pages/control-center";
import { SmsCenterPage } from "../pages/sms-center";
import { ThemesPage } from "../pages/themes";
import { DashboardPage } from "../pages/dashboard";
import { GuestsPage } from "../pages/guests";
import { ModerationPage } from "../pages/moderation";
import { GalleryPage } from "../pages/gallery";
import { WeddingSmsPage } from "../pages/wedding-sms";
import { WeddingAnalyticsPage } from "../pages/wedding-analytics";
import { SahnePage } from "../pages/sahne";
import { WeddingOrganizersPage } from "../pages/wedding-organizers";
import { WeddingThemesPage } from "../pages/wedding-themes";
import { SettingsPage } from "../pages/settings";
import { SitePage } from "../pages/site";
import { PrintPage } from "../pages/print";
import { PrivacyPage } from "../pages/privacy";

type IconType = ComponentType<{ className?: string }>;

/**
 * Global (düğün dışı) admin rotası.
 *
 * Ayrık birleşim bilinçli: nav'da GÖRÜNMEYEN rotaların (`/users/:id` gibi) sahte
 * bir ikon/etiket taşımasına gerek kalmıyor, nav'da görünenlerde ise `labelKey`
 * ve `icon` TypeScript tarafından zorunlu tutuluyor.
 */
export type AdminNavRoute = {
  showInNav: true;
  /** react-router yolu; `undefined` = index rotası. */
  routePath?: string;
  /** AppNavLink'in kullandığı mutlak yol — yalnız `paths.ts`'ten gelebilir. */
  to: RoutePath;
  /** i18n anahtarı — `scripts/validate-i18n-keys.js` bunu da denetler. */
  labelKey: string;
  icon: IconType;
  end?: boolean;
  element: ComponentType;
};

export type AdminGlobalRoute =
  | AdminNavRoute
  | { showInNav: false; routePath: string; element: ComponentType };

/** Açık tip koruyucu — `.filter()`'ın çıkarımına güvenmeden daraltır. */
export const isAdminNavRoute = (r: AdminGlobalRoute): r is AdminNavRoute => r.showInNav;

/** `/:slug/*` altına mount edilen düğün-içi rota. */
export interface AdminWeddingRoute {
  /** `/:slug` altındaki göreli segment; `undefined` = index rotası. */
  routePath?: string;
  to: (slug: string) => RoutePath;
  /** i18n anahtarı — `scripts/validate-i18n-keys.js` bunu da denetler. */
  labelKey: string;
  icon: IconType;
  end?: boolean;
  element: ComponentType;
}

export const ADMIN_GLOBAL_ROUTES: AdminGlobalRoute[] = [
  { showInNav: true, to: paths.root, labelKey: "nav.allWeddings", icon: LayoutDashboard, end: true, element: WeddingsPage },
  { showInNav: true, routePath: paths.users, to: paths.users, labelKey: "nav.users", icon: Users, element: UsersPage },
  { showInNav: true, routePath: paths.plans, to: paths.plans, labelKey: "admin.plans", icon: Package, element: PlansPage },
  { showInNav: true, routePath: paths.audit, to: paths.audit, labelKey: "admin.auditLog", icon: ScrollText, element: AuditLogPage },
  { showInNav: true, routePath: paths.support, to: paths.support, labelKey: "adminSupport.title", icon: LifeBuoy, element: AdminSupportPage },
  { showInNav: true, routePath: paths.maintenance, to: paths.maintenance, labelKey: "nav.maintenance", icon: Bug, element: MaintenancePage },
  { showInNav: true, routePath: paths.controlCenter, to: paths.controlCenter, labelKey: "nav.controlCenter", icon: SlidersHorizontal, element: ControlCenterPage },
  { showInNav: true, routePath: paths.smsCenter, to: paths.smsCenter, labelKey: "nav.smsCenter", icon: ClipboardCheck, element: SmsCenterPage },
  { showInNav: true, routePath: paths.themes, to: paths.themes, labelKey: "themes.backgrounds", icon: ImageIcon, element: ThemesPage },
  { showInNav: true, routePath: paths.site, to: paths.site, labelKey: "nav.site", icon: Store, element: SitePage },
  { showInNav: true, routePath: paths.privacy, to: paths.privacy, labelKey: "nav.privacy", icon: ShieldCheck, element: PrivacyPage },

  // Detay rotaları — nav'da yok, listeden erişilir.
  { showInNav: false, routePath: "/users/:id", element: UserDetailPage },
  { showInNav: false, routePath: "/support/:id", element: AdminSupportDetailPage },
  { showInNav: false, routePath: paths.maintenanceGroup(":fingerprint"), element: ErrorGroupDetailPage },
];

export const ADMIN_WEDDING_ROUTES: AdminWeddingRoute[] = [
  { to: paths.wedding.dashboard, labelKey: "nav.dashboard", icon: LayoutDashboard, end: true, element: DashboardPage },
  { routePath: "guests", to: paths.wedding.guests, labelKey: "nav.guests", icon: Users, element: GuestsPage },
  { routePath: "moderation", to: paths.wedding.moderation, labelKey: "nav.moderation", icon: ImageIcon, element: ModerationPage },
  { routePath: "gallery", to: paths.wedding.gallery, labelKey: "nav.gallery", icon: Images, element: GalleryPage },
  // Organizatör panelindeki nav ile aynı sıra (gallery'den sonra, sahne'den önce).
  { routePath: "sms", to: paths.wedding.sms, labelKey: "nav.sms", icon: MessageSquareText, element: WeddingSmsPage },
  { routePath: "analytics", to: paths.wedding.analytics, labelKey: "nav.analytics", icon: BarChart3, element: WeddingAnalyticsPage },
  { routePath: "print", to: paths.wedding.print, labelKey: "nav.print", icon: Printer, element: PrintPage },
  { routePath: "slideshow", to: paths.wedding.sahne, labelKey: "nav.slideshow", icon: MonitorPlay, element: SahnePage },
  { routePath: "organizers", to: paths.wedding.organizers, labelKey: "nav.organizers", icon: UserCog, element: WeddingOrganizersPage },
  { routePath: "themes", to: paths.wedding.themes, labelKey: "nav.themes", icon: Palette, element: WeddingThemesPage },
  { routePath: "settings", to: paths.wedding.settings, labelKey: "registration.title", icon: Settings, element: SettingsPage },
];

/**
 * Eski yol → yeni yol yönlendirmeleri. **BİLEREK BOŞ (2026-07-27).**
 *
 * Yönlendirmeler bir workaround'du: bayat bir rota referansını sessizce çalışır
 * gösterip asıl hatayı gizliyordu. Artık gerek yok, çünkü:
 *   1. `pnpm check:routes` (CI gate) elle yazılmış her rota string'ini yakalıyor
 *      → hiçbir kod artık bayat bir yol yazamaz, yazmaya kalkarsa merge kırılır.
 *   2. `__tests__/route-resolution.test.tsx` kayıtlı her rotanın gerçekten
 *      mount olduğunu render ederek kanıtlıyor.
 * Bu ikisiyle bir rotayı yeniden adlandırmak hiçbir çağrı noktasını unutulmuş
 * bırakamaz → yönlendirme yalnızca ÖLÜ bir yer imi köprüsüydü.
 *
 * Dizi (tip + `App.tsx`/test tüketicileriyle birlikte) BİLEREK duruyor: ileride
 * gerçekten gerekirse tek satırla geri gelir, testler kendiliğinden yeniden
 * üretilir. Boş kaldığı sürece redirect testleri sıfır vaka üretir.
 */
export const ADMIN_LEGACY_REDIRECTS: { from: string; to: RoutePath }[] = [];
